import { timeKeyForMinute } from './timelineLogic.js';

/** 临场 <3 分钟：孖下相对 4~10 基数变化超过此值（绝对值）显示方框 */
export const DOUBLE_DOWN_CHANGE_THRESHOLD = 10;

/** 临场对比基数：4~10 分钟（含） */
export const DOUBLE_DOWN_BASELINE_MINS = [4, 5, 6, 7, 8, 9, 10];

/** 临场倒计时 <3 分钟：開跑 -1、预跑小数档（-0.5…）、0~2 */
export function isLateCountdownDbMin(viewMinute) {
  if (viewMinute == null || Number.isNaN(Number(viewMinute))) return false;
  const n = Number(viewMinute);
  if (n < -1 || n >= 3) return false;
  return true;
}

export function getDoubleDownRawAtDbMin(horse, dbMin) {
  if (!horse || dbMin == null) return null;
  const key = timeKeyForMinute(dbMin);
  const ts = key ? horse.time_series?.[key] : null;
  if (!ts || !Object.prototype.hasOwnProperty.call(ts, 'double_down')) return null;
  const v = Number(ts.double_down);
  return Number.isNaN(v) ? null : v;
}

/**
 * 孖下对比基数（同一马号、固定时间轴，非上一分钟）：
 * 优先 4~10 分钟孖下平均值；若无则退化为 5 分钟单值
 */
export function getDoubleDownBaseline(horse) {
  if (!horse) return null;
  const samples = [];
  for (const m of DOUBLE_DOWN_BASELINE_MINS) {
    const v = getDoubleDownRawAtDbMin(horse, m);
    if (v != null) samples.push(v);
  }
  if (samples.length) {
    return samples.reduce((a, b) => a + b, 0) / samples.length;
  }
  const at5 = getDoubleDownRawAtDbMin(horse, 5);
  return at5 != null ? at5 : null;
}

/** 当前孖下相对固定基数（4~10 均或 5min）的变化幅度 */
export function computeDoubleDownChangeDelta(horse, viewMinute, overnightActive) {
  if (!horse || overnightActive || !isLateCountdownDbMin(viewMinute)) return 0;
  const cur = getDoubleDownRawAtDbMin(horse, viewMinute);
  if (cur == null) return 0;
  const baseline = getDoubleDownBaseline(horse);
  if (baseline == null) return 0;
  return Math.abs(cur - baseline);
}

/**
 * 孖下较大变化方框样式：变化越大边框越深、字越粗
 * @param {number} delta
 * @returns {Record<string, string>|null}
 */
export function getDoubleDownChangeHighlightStyle(delta) {
  if (delta <= DOUBLE_DOWN_CHANGE_THRESHOLD) return null;
  const excess = delta - DOUBLE_DOWN_CHANGE_THRESHOLD;
  const intensity = Math.min(1, excess / 40);
  const r = Math.round(215 + intensity * 40);
  const g = Math.round(130 - intensity * 110);
  const b = Math.round(50 - intensity * 45);
  const borderW = excess >= 30 ? 3 : excess >= 20 ? 2.5 : 2;
  const fontWeight = excess >= 30 ? '800' : excess >= 20 ? '700' : '600';
  const textColor = `rgb(${Math.round(160 - intensity * 90)}, ${Math.round(35 - intensity * 25)}, ${Math.round(35 - intensity * 20)})`;
  return {
    boxSizing: 'border-box',
    border: `${borderW}px solid rgb(${r}, ${g}, ${b})`,
    fontWeight,
    color: textColor,
  };
}

export function getDoubleDownChangeBoxStyleForHorse(horse, viewMinute, overnightActive) {
  const delta = computeDoubleDownChangeDelta(horse, viewMinute, overnightActive);
  return getDoubleDownChangeHighlightStyle(delta);
}
export function formatDoubleDisplayValue(value) {
  if (value == null || value === '') return '';
  const numValue = Number(value);
  if (Number.isNaN(numValue) || numValue <= 0) return '';
  if (numValue < 10) return numValue.toFixed(1);
  return String(Math.round(numValue));
}

/** 与频谱表 formatDoubleValue 一致：&lt;10 保留小数，10+ 四舍五入 */
function doubleColorBandValue(raw) {
  const num = Number(raw);
  if (Number.isNaN(num) || num < 0) return null;
  if (num < 10) return num;
  return Math.round(num);
}

/**
 * 孖上/孖下泡泡底色（与 HorseTable getDoubleUpBackground 一致）
 * &lt;10：灰色，越小越浅；10+ 按显示值分档
 */
export function getDoubleUpBubbleBackground(value) {
  const num = Number(value);
  if (Number.isNaN(num) || num < 0) return 'transparent';
  const band = doubleColorBandValue(num);
  if (band == null) return 'transparent';
  if (band < 10) {
    const ratio = Math.max(0, Math.min(1, num / 10));
    const gray = Math.round(228 - ratio * 58);
    return `rgb(${gray}, ${gray}, ${gray})`;
  }
  if (band >= 10 && band < 20) return 'rgb(128, 255, 255)';
  if (band >= 20 && band < 30) return 'rgba(0, 255, 64)';
  if (band >= 30) return 'rgb(255, 154, 154)';
  return 'transparent';
}

export function getViewTimeKey(viewMinute, overnightActive, sampleHorse) {
  if (overnightActive) {
    const keys = Object.keys(sampleHorse?.time_series || {})
      .filter((key) => {
        const minute = parseInt(String(key).split('_')[1], 10);
        return !Number.isNaN(minute) && minute > 720;
      })
      .sort((a, b) => parseInt(b.split('_')[1], 10) - parseInt(a.split('_')[1], 10));
    return keys[0] || null;
  }
  return timeKeyForMinute(viewMinute);
}

export function getDoubleUpAtView(horse) {
  const base = horse?.odds || {};
  return base.double_up;
}

export function getDoubleDownAtView(horse, viewMinute, overnightActive) {
  if (!horse) return null;
  const tk = getViewTimeKey(viewMinute, overnightActive, horse);
  const ts = tk ? horse.time_series?.[tk] : null;
  if (ts && Object.prototype.hasOwnProperty.call(ts, 'double_down')) {
    const v = Number(ts.double_down);
    if (!Number.isNaN(v) && v > 0) return ts.double_down;
  }
  return null;
}

export function buildChartDbBubbleItem(horse, viewMinute, overnightActive) {
  const upRaw = getDoubleUpAtView(horse, viewMinute, overnightActive);
  const downRaw = getDoubleDownAtView(horse, viewMinute, overnightActive);
  const upText = formatDoubleDisplayValue(upRaw);
  const downText = formatDoubleDisplayValue(downRaw);
  const downChangeStyle = getDoubleDownChangeBoxStyleForHorse(horse, viewMinute, overnightActive);
  return {
    number: horse?.number,
    upText,
    downText,
    upBg: upText ? getDoubleUpBubbleBackground(upRaw) : 'transparent',
    downBg: downText ? getDoubleUpBubbleBackground(downRaw) : 'transparent',
    downChangeStyle: downChangeStyle || {},
  };
}
